import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleDot } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { MessageAttachments } from '../../../../components/social/MessageAttachments';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { ChannelIcon } from '../../../../components/ui/ChannelIcon';
import { CHANNEL_LABEL, formatExactTimestamp } from '../../../../lib/inboxView';
import { formatRelativeTime } from '../../../../lib/relativeTime';
import { useLiveRefreshOnOpen } from '../../../../lib/useLiveRefreshOnOpen';
import {
  dmReplyWindowState,
  dmWindowBlocksSend,
  dmWindowClosedMessage,
  dmWindowPassedWarning,
} from '../../../../lib/inboxDmWindow';
import { socialApi, type ClientMessage, type ClientThread } from './socialApi';
import { SocialFrame } from './SocialFrame';
import { CommentDetail } from './CommentDetail';

// /portal/social/inbox — comments, reviews and direct messages for the club.
// ⚠️ Members of the public write privately here. The scope is the server's; the
// page just renders what it is given. ⚠️ A platform refusal (above all Instagram's
// reply window) is shown VERBATIM and prominently, never swallowed: an unexplained
// failure reads as a broken product, while "Instagram does not allow replies to
// this conversation any more" reads as Instagram. ⚠️ AI replies are ON-DEMAND:
// only the "Suggest a reply" button calls it.

const KIND: Record<string, string> = { comment: 'Comment', review: 'Review', dm: 'Message' };

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Try again.';
}

// Reviews and direct messages. A comment is a conversation about a POST, so it has
// its own host (CommentDetail) built on the staff inbox's post-group components.
function ConversationDetail({ threadId, onChanged }: { threadId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['social-thread', threadId], queryFn: () => socialApi.thread(threadId), retry: false });
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [failure, setFailure] = useState<string | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['social-thread', threadId] });
    onChanged();
  };

  const send = useMutation({
    mutationFn: (v: { thread: ClientThread; text: string; target?: string }) => {
      if (v.thread.kind === 'dm') return socialApi.sendDm(v.thread.id, v.text);
      if (v.thread.kind === 'review') return socialApi.replyReview(v.thread.id, v.text);
      return socialApi.replyComment(v.thread.id, v.text, v.target);
    },
    onSuccess: () => {
      setDraft('');
      setSuggestions([]);
      setFailure(null);
      refresh();
    },
    // The server's own sentence, verbatim.
    onError: (e) => setFailure(errorText(e)),
  });
  const status = useMutation({
    mutationFn: (s: 'new' | 'resolved') => socialApi.setStatus(threadId, s),
    onSuccess: refresh,
    onError: (e) => setFailure(errorText(e)),
  });
  const suggest = useMutation({
    mutationFn: (messageId?: string) => socialApi.suggestReply(threadId, messageId),
    onSuccess: (r) => setSuggestions(r.drafts),
    onError: (e) => setFailure(errorText(e)),
  });

  if (q.isPending) return <CenteredSpinner />;
  if (q.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        That conversation was not found.
      </p>
    );
  }
  const { thread, messages } = q.data;
  const lastIncoming = [...messages].reverse().find((m: ClientMessage) => m.direction === 'incoming' && m.text.trim() !== '');

  // DM reply window (shared with the staff composer). Facebook blocks past the
  // window; Instagram WARNS (it may be a message request) and lets the server decide.
  const window =
    thread.kind === 'dm' ? dmReplyWindowState(thread.channel, thread.lastInboundAt, Date.now(), 'agent') : { kind: 'unknown' as const };
  const blocked = thread.kind === 'dm' && window.kind === 'closed' && dmWindowBlocksSend(thread.channel);
  const passed = thread.kind === 'dm' && window.kind === 'closed' && !blocked;
  const alreadyReplied = thread.kind === 'review' && thread.hasReply === true;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ChannelIcon channel={thread.channel} />
        <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold">{CHANNEL_LABEL[thread.channel]}</span>
        <span className="text-xs text-muted-foreground">{KIND[thread.kind]} from {thread.author.name || 'a customer'}</span>
        <span className="text-xs text-muted-foreground" title={formatExactTimestamp(thread.lastActivityAt)}>
          {formatRelativeTime(thread.lastActivityAt)}
        </span>
        {thread.kind === 'review' && thread.rating !== null && <span className="text-xs">{'★'.repeat(thread.rating)}</span>}
        <span className="text-xs text-muted-foreground">Status: {thread.status}</span>
        <Button size="sm" variant="outline" disabled={status.isPending} onClick={() => status.mutate(thread.status === 'resolved' ? 'new' : 'resolved')}>
          {thread.status === 'resolved' ? 'Reopen' : 'Mark resolved'}
        </Button>
        {thread.permalink && (
          <a className="text-xs underline" href={thread.permalink} target="_blank" rel="noreferrer">
            View on {CHANNEL_LABEL[thread.channel]}
          </a>
        )}
      </div>

      <ul className="space-y-2" data-testid="messages">
        {messages.map((m) => (
          <li key={m.id} className={`max-w-[85%] rounded-md p-2 text-sm ${m.direction === 'outgoing' ? 'ml-auto bg-muted' : 'border border-border'}`}>
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {m.direction === 'incoming' && thread.author.avatarUrl && (
                <img src={thread.author.avatarUrl} alt="" className="h-5 w-5 rounded-full object-cover" />
              )}
              {m.direction === 'outgoing' ? 'You' : m.authorName}
            </p>
            {m.text && <p className="whitespace-pre-wrap text-foreground">{m.text}</p>}
            <MessageAttachments attachments={m.attachments} />
          </li>
        ))}
      </ul>

      {blocked && (
        <p role="status" className="rounded-md border border-tone-warning-foreground/40 bg-tone-warning px-3 py-2 text-sm text-tone-warning-foreground" data-testid="dm-window-closed">
          {dmWindowClosedMessage(thread.channel)}
        </p>
      )}
      {passed && (
        <p role="status" className="rounded-md border border-tone-warning-foreground/40 bg-tone-warning px-3 py-2 text-sm text-tone-warning-foreground" data-testid="dm-window-passed">
          {dmWindowPassedWarning(thread.channel)}
        </p>
      )}
      {alreadyReplied && <p className="text-sm text-muted-foreground">This review already has a reply from you.</p>}

      {failure && (
        <p role="alert" className="rounded-md border-2 border-destructive px-3 py-2 text-sm text-destructive" data-testid="send-failure">
          {failure}
        </p>
      )}

      {!blocked && !alreadyReplied && (
        <div>
          <Button type="button" variant="outline" size="sm" disabled={suggest.isPending || !lastIncoming} onClick={() => suggest.mutate(lastIncoming?.id)}>
            {suggest.isPending ? 'Thinking…' : 'Suggest a reply (AI)'}
          </Button>
          <span className="ml-2 text-xs text-muted-foreground">AI drafts are suggestions. Read them and check any facts before sending.</span>
          {suggestions.length > 0 && (
            <ul className="mt-2 space-y-2" data-testid="reply-suggestions">
              {suggestions.map((s, i) => (
                <li key={i} className="rounded-md border border-border p-2 text-sm">
                  <p className="whitespace-pre-wrap">{s}</p>
                  <Button className="mt-1" size="sm" type="button" onClick={() => setDraft(s)}>
                    Use this one
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <textarea
            className="mt-2 w-full rounded-md border border-input bg-background p-2 text-sm text-foreground"
            rows={4}
            placeholder={thread.kind === 'review' ? 'Reply publicly to this review' : thread.kind === 'dm' ? 'Write a message' : 'Reply publicly to this comment'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <Button className="mt-2" disabled={send.isPending || draft.trim() === ''} onClick={() => send.mutate({ thread, text: draft, target: lastIncoming?.id })}>
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
          {thread.kind !== 'dm' && <span className="ml-2 text-xs text-muted-foreground">This reply is public.</span>}
        </div>
      )}
    </div>
  );
}

// Comments hand off to CommentDetail; reviews and messages keep their own pane.
function ThreadDetail({ threadId, onChanged, onSelectThread }: { threadId: string; onChanged: () => void; onSelectThread: (id: string) => void }) {
  const q = useQuery({ queryKey: ['social-thread', threadId], queryFn: () => socialApi.thread(threadId), retry: false });
  const qc = useQueryClient();
  // Refresh-on-open: pull native Facebook / Instagram replies for this thread live.
  useLiveRefreshOnOpen({
    scope: 'portal',
    threadId,
    enabled: !!q.data && q.data.thread.kind !== 'review',
    refresh: socialApi.refreshThread,
    onNewMessages: () => {
      void qc.invalidateQueries({ queryKey: ['social-thread'] });
      void qc.invalidateQueries({ queryKey: ['social-post-group'] });
      onChanged();
    },
  });
  if (q.isPending) return <CenteredSpinner />;
  if (q.isError) {
    return (
      <p className="text-sm text-destructive" role="alert">
        That conversation was not found.
      </p>
    );
  }
  return q.data.thread.kind === 'comment' ? (
    <CommentDetail threadId={threadId} onChanged={onChanged} onSelectThread={onSelectThread} />
  ) : (
    <ConversationDetail threadId={threadId} onChanged={onChanged} />
  );
}

function InboxBody({ accountId }: { accountId: string }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('new');
  const [channel, setChannel] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['social-threads', accountId, kind, status, channel],
    queryFn: () => socialApi.threads(accountId, { kind: kind || undefined, status: status || undefined, channel: channel || undefined }),
    retry: false,
    refetchInterval: 30000,
  });
  // The unresolved total for the whole location (new + pending), whatever the filter
  // below says, so it never reads as "0" just because you are looking at Resolved.
  // pageSize 1: only the total is wanted.
  const unresolvedNew = useQuery({ queryKey: ['social-threads', 'count', accountId, 'new'], queryFn: () => socialApi.threads(accountId, { status: 'new', pageSize: 1 }), retry: false, refetchInterval: 30000 });
  const unresolvedPending = useQuery({ queryKey: ['social-threads', 'count', accountId, 'pending'], queryFn: () => socialApi.threads(accountId, { status: 'pending', pageSize: 1 }), retry: false, refetchInterval: 30000 });
  const unresolved = unresolvedNew.data && unresolvedPending.data ? unresolvedNew.data.total + unresolvedPending.data.total : null;
  const select = 'rounded-md border border-input bg-background px-2 py-1 text-sm';

  return (
    <div className="grid gap-4 md:min-h-0 md:flex-1 md:grid-cols-[22rem_1fr]">
      <div className="md:min-h-0 md:overflow-y-auto md:pr-1">
        {unresolved !== null && (
          <p
            className={`mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${unresolved > 0 ? 'bg-tone-warning text-tone-warning-foreground' : 'bg-muted text-muted-foreground'}`}
            data-testid="unresolved-total"
          >
            <CircleDot className="h-3.5 w-3.5" />
            {unresolved} unresolved
          </p>
        )}
        <div className="mb-3 flex flex-wrap gap-2">
          <select aria-label="Type" className={select} value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">All types</option>
            <option value="comment">Comments</option>
            <option value="review">Reviews</option>
            <option value="dm">Messages</option>
          </select>
          <select aria-label="Channel" className={select} value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option value="">All channels</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="metaads">Ad comments</option>
            <option value="googlebusiness">Google Business</option>
          </select>
          <select aria-label="Status" className={select} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="new">New</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="">Any status</option>
          </select>
        </div>
        {list.isPending ? (
          <CenteredSpinner />
        ) : list.isError ? (
          <p className="text-sm text-destructive" role="alert">
            Could not load your inbox. Try again.
          </p>
        ) : list.data.threads.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing here. If you expected messages, ask Kyck to check that the inbox is switched on for this location.</p>
        ) : (
          <ul className="space-y-2" data-testid="thread-list">
            {list.data.threads.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setSelected(t.id)}
                  className={`w-full rounded-md border p-2 text-left text-sm ${selected === t.id ? 'border-foreground' : 'border-border'}`}
                >
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ChannelIcon channel={t.channel} className="h-3.5 w-3.5" />
                    {CHANNEL_LABEL[t.channel]} · {KIND[t.kind]} · {t.status}
                    <span className="ml-auto shrink-0" title={formatExactTimestamp(t.lastActivityAt)}>
                      {formatRelativeTime(t.lastActivityAt)}
                    </span>
                  </span>
                  <span className="block font-medium text-foreground">{t.author.name || 'Customer'}</span>
                  <span className="line-clamp-2 text-muted-foreground">{t.preview}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="md:min-h-0 md:overflow-y-auto">
        {selected ? (
          <ThreadDetail
            key={selected}
            threadId={selected}
            onSelectThread={setSelected}
            onChanged={() => void qc.invalidateQueries({ queryKey: ['social-threads'] })}
          />
        ) : (
          <p className="text-sm text-muted-foreground">Choose a conversation.</p>
        )}
      </div>
    </div>
  );
}

export function InboxPage() {
  return <SocialFrame tab="inbox">{(accountId) => <InboxBody key={accountId} accountId={accountId} />}</SocialFrame>;
}
